
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, Heart, Clock, MapPin } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { getRestaurantStatus } from '@/utils/restaurantStatus';
import { useLocationPermission } from '@/hooks/useLocationPermission';
import { formatDistance } from '@/utils/distanceCalculation';
import { getDistanceFromAddress } from '@/utils/gambianLocations';

interface Restaurant {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rating: number;
  address: string;
  created_at: string;
  is_open: boolean;
  opening_hours: any;
  average_rating?: number;
  avg_prep_time_min?: number;
  avg_prep_time_max?: number;
}

interface RestaurantCardProps {
  restaurant: Restaurant;
  onClick: () => void;
  variant?: 'horizontal' | 'vertical';
  showFavorite?: boolean;
}

const RestaurantCard = ({ restaurant, onClick, variant = 'horizontal', showFavorite = false }: RestaurantCardProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { location } = useLocationPermission();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [priceRange, setPriceRange] = useState<string>('');
  const [distance, setDistance] = useState<string>('');
  const [hasFreeDelivery, setHasFreeDelivery] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  
  const statusInfo = getRestaurantStatus(restaurant);
  const displayRating = restaurant.average_rating || restaurant.rating;

  // Get preparation time display from database with fallbacks
  const prepTimeMin = restaurant.avg_prep_time_min || 25;
  const prepTimeMax = restaurant.avg_prep_time_max || 35;
  const prepTimeDisplay = `${prepTimeMin}-${prepTimeMax} min`;

  useEffect(() => {
    if (user && showFavorite) {
      checkFavoriteStatus();
    }
    fetchPriceRange();
    calculateRestaurantDistance();
    checkFreeDeliveryPromo();
  }, [user, restaurant.id, showFavorite, location]);

  const checkFreeDeliveryPromo = async () => {
    // Restaurants with 10% off promotion
    const tenPercentOffIds = [
      '0d29453a-9312-405c-b581-954fe0d2368f', // Kadie Kadie
      'c625ff68-b35b-4548-9a34-c3a68265b2a0', // PYFC Kitchen
      'aec16016-e8d4-4991-85f4-3ac225b64062', // Baa Ani Dingo Restaurant
    ];

    if (tenPercentOffIds.includes(restaurant.id)) {
      setDiscountPercentage(10);
    }
    
    try {
      const { data, error } = await supabase
        .from('promos')
        .select('*')
        .eq('restaurant_id', restaurant.id)
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString())
        .lte('start_date', new Date().toISOString());

      if (!error && data && data.length > 0) {
        // Check for free delivery
        const freeDeliveryPromo = data.find(p => p.discount_type === 'free_delivery');
        if (freeDeliveryPromo) {
          setHasFreeDelivery(true);
        }
        
        // Check for percentage discount from database promos
        const percentagePromo = data.find(p => p.discount_type === 'percentage');
        if (percentagePromo) {
          setDiscountPercentage(percentagePromo.discount_value);
        }
      }
    } catch (error) {
      console.error('Error checking promos:', error);
    }
  };

  const calculateRestaurantDistance = () => {
    if (!location || !restaurant.address) {
      // Always show a default distance for restaurants in Greater Banjul Area
      setDistance('~5km');
      return;
    }

    try {
      // Use the new location lookup system
      const result = getDistanceFromAddress(
        location.latitude,
        location.longitude,
        restaurant.address
      );

      if (result.distance > 0) {
        setDistance(formatDistance(result.distance));
      } else {
        // Fallback distance for Greater Banjul Area
        setDistance('~5km');
      }
    } catch (error) {
      console.error('Error calculating distance to restaurant:', error);
      setDistance('~5km');
    }
  };

  const checkFavoriteStatus = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('restaurant_id', restaurant.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking favorite status:', error);
        return;
      }

      setIsFavorite(!!data);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const fetchPriceRange = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select('price')
        .eq('restaurant_id', restaurant.id)
        .eq('is_available', true);

      if (error) {
        console.error('Error fetching price range:', error);
        return;
      }

      if (data && data.length > 0) {
        const prices = data.map(item => Number(item.price));
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        
        if (minPrice === maxPrice) {
          setPriceRange(`D${minPrice}`);
        } else {
          setPriceRange(`D${minPrice} - D${maxPrice}`);
        }
      }
    } catch (error) {
      console.error('Error fetching price range:', error);
    }
  };

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save favorites",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      if (isFavorite) {
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('restaurant_id', restaurant.id);

        if (error) throw error;
        setIsFavorite(false);
        toast({
          title: "Removed from favorites",
          description: `${restaurant.name} has been removed from your favorites`,
        });
      } else {
        const { error } = await supabase
          .from('user_favorites')
          .insert({
            user_id: user.id,
            restaurant_id: restaurant.id
          });

        if (error) throw error;
        setIsFavorite(true);
        toast({
          title: "Added to favorites",
          description: `${restaurant.name} has been added to your favorites`,
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Error",
        description: "Could not update favorites. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'vertical') {
    return (
      <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={onClick}>
        <CardContent className="p-0">
          <div className="relative h-40 bg-gray-200 rounded-t-lg overflow-hidden">
            {restaurant.image_url ? (
              <img
                src={restaurant.image_url}
                alt={restaurant.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                No Image
              </div>
            )}
            
            {/* Promo Banners */}
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              {hasFreeDelivery && (
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  FREE DELIVERY
                </div>
              )}
              {discountPercentage > 0 && (
                <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  {discountPercentage}% OFF
                </div>
              )}
            </div>
            
            {/* Status indicator */}
            <div className="absolute top-2 right-2">
              <div className={`w-3 h-3 rounded-full ${statusInfo.statusColor}`} />
            </div>
            
            {/* Favorite button */}
            {showFavorite && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 left-2 bg-white/80 hover:bg-white p-1 h-8 w-8"
                onClick={toggleFavorite}
                disabled={loading}
              >
                <Heart 
                  className={`h-4 w-4 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} 
                />
              </Button>
            )}
          </div>
          
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-base truncate flex-1">{restaurant.name}</h3>
              <span className={`text-xs px-2 py-1 rounded-full ml-2 ${
                statusInfo.status === 'open' ? 'bg-green-100 text-green-800' : 
                statusInfo.status === 'closing_soon' ? 'bg-yellow-100 text-yellow-800' : 
                'bg-red-100 text-red-800'
              }`}>
                {statusInfo.statusText}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{restaurant.description}</p>
            
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-medium">{displayRating.toFixed(1)}</span>
              </div>
              {distance && (
                <div className="flex items-center gap-1 text-gray-500">
                  <MapPin className="h-3 w-3" />
                  <span className="text-xs">{distance}</span>
                </div>
              )}
              <div className="flex items-center gap-1 text-gray-500">
                <Clock className="h-4 w-4" />
                <span className="text-sm">{prepTimeDisplay}</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              {priceRange && (
                <span className="text-sm font-medium text-gray-700">{priceRange}</span>
              )}
              <div className="flex items-center gap-1 text-gray-500">
                <MapPin className="h-3 w-3" />
                <span className="text-xs truncate">{restaurant.address}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Horizontal variant
  return (
    <Card
      className="flex-shrink-0 w-64 cursor-pointer hover:shadow-lg transition-shadow"
      onClick={onClick}
    >
      <CardContent className="p-0">
        <div className="relative h-32 bg-gray-200 rounded-t-lg overflow-hidden">
          {restaurant.image_url ? (
            <img
              src={restaurant.image_url}
              alt={restaurant.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              No Image
            </div>
          )}
          
          {/* Promo Banners */}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {hasFreeDelivery && (
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                FREE DELIVERY
              </div>
            )}
            {discountPercentage > 0 && (
              <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                {discountPercentage}% OFF
              </div>
            )}
          </div>
          
          <div className="absolute top-2 right-2">
            <div className={`w-3 h-3 rounded-full ${statusInfo.statusColor}`} />
          </div>
        </div>
        <div className="p-3">
          <div className="flex items-start justify-between mb-1">
            <h3 className="font-semibold text-sm truncate flex-1">{restaurant.name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${
              statusInfo.status === 'open' ? 'bg-green-100 text-green-800' : 
              statusInfo.status === 'closing_soon' ? 'bg-yellow-100 text-yellow-800' : 
              'bg-red-100 text-red-800'
            }`}>
              {statusInfo.statusText}
            </span>
          </div>
          <p className="text-xs text-gray-600 mb-2 line-clamp-2">{restaurant.description}</p>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="text-xs font-medium">{displayRating.toFixed(1)}</span>
            </div>
            {distance && (
              <div className="flex items-center gap-1 text-gray-500">
                <MapPin className="h-3 w-3" />
                <span className="text-xs">{distance}</span>
              </div>
            )}
            <span className="text-xs text-gray-500">{prepTimeDisplay}</span>
          </div>
          <div className="flex items-center justify-between">
            {priceRange && (
              <span className="text-xs font-medium text-gray-700">{priceRange}</span>
            )}
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-500 truncate">{restaurant.address}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RestaurantCard;
